import { FormGroup } from "@angular/forms";
import { Location } from '@angular/common';
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { BehaviorSubject, Subject } from "rxjs";
import { VisitorRegistrationFormStatus } from "../../../models/visitor-registration-form-status";
import { Component, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BinaryService } from "../../../services/data/binary.service";
import { RegistrationFormService } from "../../../services/data/registration-form.service";
import { AuthUserService } from "../../../services/auth-user.service";
import { ToastService } from "../../../services/toast.service";
import { ConfirmModalService } from "../../confirm-modal/confirm-modal.service";
import { VisitorRegistrationFormType } from "../../../models/visitor-registration-form-type";

@Component({ template: '' })
export abstract class BaseRegistrationFormComponent {
    private toastService = inject(ToastService);
    private authUserService = inject(AuthUserService);
    private route = inject(ActivatedRoute);
    private _router = inject(Router);
    private binaryService = inject(BinaryService);
    private _location = inject(Location);
    private formService = inject(RegistrationFormService);
    private _confirmModalService = inject(ConfirmModalService);

    protected loading$: Subject<boolean> = new BehaviorSubject<boolean>(true);

    protected formId!: string;
    protected model: any = {};
    protected origModel: any = {};

    protected form = new FormGroup({});
    protected fields: FormlyFieldConfig[] = [];
    protected options: FormlyFormOptions = {
        formState: {
            disabled: true
        }
    };

    constructor(protected formType: VisitorRegistrationFormType) {
        // Handle loading signal
        this.loading$.subscribe((value) => this.onLoadingChange(value));
    }

    ngOnInit(): void {
        this.formId = this.route.snapshot.paramMap.get('id')!;
        this.load();
    }

    public hasUnsavedChanges(): boolean {
        if (this.form.dirty) {
            let result = confirm('You have unsaved changes. Are you sure you want to leave?');
            return !result;
        }

        return false;
    }

    get showWithdrawButton(): Boolean {
        if (!this.model?.form || !this.isOwnForm) {
            return false;
        }

        if (this.model.form.status === VisitorRegistrationFormStatus.Submitted ||
            this.model.form.status === VisitorRegistrationFormStatus.Approved ||
            this.model.form.status === VisitorRegistrationFormStatus.ApprovedForReview ||
            this.model.form.status === VisitorRegistrationFormStatus.Rejected ) {
            return true;
        }

        return false;
    }

    get showSaveButton(): Boolean {
        if (!this.model?.form) {
            return false;
        }

        if (this.model.form.status === VisitorRegistrationFormStatus.Submitted ||
            this.model.form.status === VisitorRegistrationFormStatus.InReview ||
            this.model.form.status === VisitorRegistrationFormStatus.Approved ||
            this.model.form.status === VisitorRegistrationFormStatus.ApprovedForReview ||
            this.model.form.status === VisitorRegistrationFormStatus.Archived) {
            return false;
        }

        return true;
    }

    get showDeleteButton(): Boolean {
        if (!this.model?.form) {
            return false;
        }

        return (this.model.form.status === VisitorRegistrationFormStatus.New || this.model.form.status === VisitorRegistrationFormStatus.Draft);
    }

    get isOwnForm(): Boolean {
        return this.model?.user?.email === this.authUserService.email;
    }

    navigateHome() {
        this._router.navigate(['']);
    }

    onModelChange() {
        if (!this.model) { return; }

        if (this.model.passport) {
            this.onPassportChange();
        }

        if (this.model.businessVisa) {
            this.onBusinessVisaChange();
        }

        if (this.model.workVisa) {
            this.onWorkVisaChange();
        }

        if (this.model.workPermit) {
            this.onWorkPermitChange();
        }

        if (this.model.sipsi) {
            this.onSipsiChange();
        }

        if (this.model.documentation?.btpCardFileDescriptor) {
            this.onBtpCardChange();
        }

        if (this.model.documentation?.ehsCertificateFileDescriptor) {
            this.onEHSCertificateChange();
        }

        if (this.model.documentation?.technicalSkillsFileDescriptor) {
            this.onTechSkillsChange();
        }

        if (this.model.documentation?.medicalCertificateFileDescriptor) {
            this.onMedicalCertChange();
        }

        if (this.model.documentation?.assignmentContractFileDescriptor) {
            this.onAssignmentContractChange();
        }

        if (this.model.documentation?.anfasN1FileDescriptor) {
            this.onANFASN1Change();
        }

        if (this.model.documentation?.safetyInductionFileDescriptor) {
            this.onsafetyInductionFileChange();
        }
    }

    onANFASN1Change() {
        if (this.origModel.documentation && this.origModel.documentation.anfasN1FileDescriptor === this.model.documentation.anfasN1FileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.anfasN1FileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.anfasN1FileDescriptor instanceof FileList) {
            let file = (this.model.documentation.anfasN1FileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.anfasN1FileDescriptor;
                    updateModel.documentation.anfasN1FileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onsafetyInductionFileChange() {
        if (this.origModel.documentation && this.origModel.documentation.safetyInductionFileDescriptor === this.model.documentation.safetyInductionFileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.safetyInductionFileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.safetyInductionFileDescriptor instanceof FileList) {
            let file = (this.model.documentation.safetyInductionFileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.safetyInductionFileDescriptor;
                    updateModel.documentation.safetyInductionFileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }
    onEHSCertificateChange() {
        if (this.origModel.documentation && this.origModel.documentation.ehsCertificateFileDescriptor === this.model.documentation.ehsCertificateFileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.ehsCertificateFileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.ehsCertificateFileDescriptor instanceof FileList) {
            let file = (this.model.documentation.ehsCertificateFileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.ehsCertificateFileDescriptor;
                    updateModel.documentation.ehsCertificateFileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onBtpCardChange() {
        if (this.origModel.documentation && this.origModel.documentation.btpCardFileDescriptor === this.model.documentation.btpCardFileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.btpCardFileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.btpCardFileDescriptor instanceof FileList) {
            let file = (this.model.documentation.btpCardFileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.btpCardFileDescriptor;
                    updateModel.documentation.btpCardFileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onTechSkillsChange() {
        if (this.origModel.documentation && this.origModel.documentation.technicalSkillsFileDescriptor === this.model.documentation.technicalSkillsFileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.technicalSkillsFileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.technicalSkillsFileDescriptor instanceof FileList) {
            let file = (this.model.documentation.technicalSkillsFileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.technicalSkillsFileDescriptor;
                    updateModel.documentation.technicalSkillsFileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onMedicalCertChange() {
        if (this.origModel.documentation && this.origModel.documentation.medicalCertificateFileDescriptor === this.model.documentation.medicalCertificateFileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.medicalCertificateFileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.medicalCertificateFileDescriptor instanceof FileList) {
            let file = (this.model.documentation.medicalCertificateFileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.medicalCertificateFileDescriptor;
                    updateModel.documentation.medicalCertificateFileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onAssignmentContractChange() {
        if (this.origModel.documentation && this.origModel.documentation.assignmentContractFileDescriptor === this.model.documentation.assignmentContractFileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.documentation.assignmentContractFileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.documentation.assignmentContractFileDescriptor instanceof FileList) {
            let file = (this.model.documentation.assignmentContractFileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.documentation.assignmentContractFileDescriptor;
                    updateModel.documentation.assignmentContractFileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onSipsiChange() {
        if (this.origModel.sipsi && this.origModel.sipsi.fileDescriptor === this.model.sipsi.fileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.sipsi.fileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.sipsi.fileDescriptor instanceof FileList) {
            let file = (this.model.sipsi.fileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.sipsi.fileDescriptor;
                    updateModel.sipsi.fileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onWorkPermitChange() {
        if (this.origModel.workPermit && this.origModel.workPermit.fileDescriptor === this.model.workPermit.fileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.workPermit.fileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.workPermit.fileDescriptor instanceof FileList) {
            let file = (this.model.workPermit.fileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.workPermit.fileDescriptor;
                    updateModel.workPermit.fileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onWorkVisaChange() {
        if (this.origModel.workVisa && this.origModel.workVisa.fileDescriptor === this.model.workVisa.fileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.workVisa.fileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.workVisa.fileDescriptor instanceof FileList) {
            let file = (this.model.workVisa.fileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.workVisa.fileDescriptor;
                    updateModel.workVisa.fileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onBusinessVisaChange() {
        if (this.origModel.businessVisa && this.origModel.businessVisa.fileDescriptor === this.model.businessVisa.fileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.businessVisa.fileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.businessVisa.fileDescriptor instanceof FileList) {
            let file = (this.model.businessVisa.fileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe(
                fileDescriptor => {
                    let updateModel = { ...this.model };
                    delete updateModel.businessVisa.fileDescriptor;
                    updateModel.businessVisa.fileDescriptor = fileDescriptor;
                    this.model = updateModel;
                }
            );
        }
    }

    onPassportChange() {
        if (this.origModel.passport && this.origModel.passport.fileDescriptor === this.model.passport.fileDescriptor) {
            return;
        }

        // File was removed
        if (this.model.passport.fileDescriptor == null) {
            this.save();
            return;
        }

        // Upload new file
        if (this.model.passport.fileDescriptor instanceof FileList) {
            let file = (this.model.passport.fileDescriptor as FileList)[0];
            this.binaryService.uploadBinary(this.formId, file).subscribe({
                next: (fileDescriptor) => {
                    let updateModel = { ...this.model };
                    delete updateModel.passport.fileDescriptor;
                    updateModel.passport.fileDescriptor = fileDescriptor;
                    this.model = updateModel;
                },
                error: (error) => {
                    console.log(error);
                }
            });
        }
    }

    onSubmit(eventSubmitterId: string) {
        if (eventSubmitterId === 'save') {
            this.save();
        } else if (eventSubmitterId === 'copy') {
            this.copy();
        } else if (eventSubmitterId === 'submit') {
            this.submit();
        } else if (eventSubmitterId === 'withdraw') {
            this.withdraw();
        } else if (eventSubmitterId === 'delete') {
            this.delete();
        }
    }

    private navigateTo(formId: string, formType: VisitorRegistrationFormType) {
        if (formType === VisitorRegistrationFormType.BusinessTrip) {
            this._router.navigate(['/form/stv/', formId, {}]);
        } else if (formType === VisitorRegistrationFormType.WorkOnSite) {
            this._router.navigate(['/form/sta/', formId, {}]);
        }
    }

    private copy() {
        this._confirmModalService.showConfirmDialog('Are you sure you want to make a copy of the form?', 'Copy form?').subscribe(
            (dialogResult) => {
                if (dialogResult.result === false) {
                    return;
                }

                this.loading$.next(true);
                this.formService.copyForm(this.formId).subscribe({
                    next: (copyResponse) => {
                        this.showToaster('Copied');
                        this.navigateTo(copyResponse.id, copyResponse.formType);
                    }
                })
            }
        )
    }

    private load() {
        this.loading$.next(true);
        this.formService.getForm(this.formId).subscribe({
            next: (data) => {
                this.updateModelSnapshot(data);
            }
        })
    }

    private delete() {
        this._confirmModalService.showConfirmDelete().subscribe(
            (dialogResult) => {
                if (dialogResult.result === false) {
                    return;
                }

                this.loading$.next(true);
                this.formService.deleteForm(this.formId).subscribe({
                    next: () => {
                        this.loading$.next(false);
                        this.showToaster('You have deleted the form.');
                        this._router.navigate(['']);
                    }
                })
            }
        )
    }

    private submit() {
        if (!this.form.valid) {
            return;
        }

        this._confirmModalService.showConfirmDialog('Are you sure you want to submit?', 'Ready to submit?').subscribe(
            (dialogResult) => {
                if (dialogResult.result === false) {
                    return;
                }

                this.loading$.next(true);
                this.formService.submitForm(this.formId, this.model).subscribe({
                    next: (data) => {
                        this.updateModelSnapshot(data);
                        this.showToaster('You have successfully submitted the form.');
                    }
                })
            }
        )
    }

    private withdraw() {
        this._confirmModalService.showConfirmDialog('Are you sure you want to withdraw the form?', 'Withdraw form?').subscribe(
            (dialogResult) => {
                if (dialogResult.result === false) {
                    return;
                }

                this.loading$.next(true);
                this.formService.withdrawForm(this.formId).subscribe({
                    next: (data) => {
                        this.updateModelSnapshot(data);
                        this.showToaster('You have successfully withdrawn the form.');
                    }
                })
            }
        )
    }

    private save() {
        this.loading$.next(true);
        this.formService.updateForm(this.formId, this.model).subscribe({
            next: (data) => {
                this.updateModelSnapshot(data);
                this.showToaster('Saved');
            }
        })
    }

    private updateModelSnapshot(data: any) {
        this.model = data;
        this.origModel = structuredClone(data);
        this.loading$.next(false);
    }

    private showToaster(message: string, title: string = '') {
        this.toastService.show(title, message, {
            classname: 'bg-success text-light',
            delay: 2000,
            autohide: true,
        });
    }

    private onLoadingChange(value: boolean) {
        if (value === true) {
            this.options.formState.disabled = true;
            return;
        }

        try {
            if (!this.model?.form) {
                this.options.formState.disabled = true;
                return;
            }

            if (this.model.form.status === VisitorRegistrationFormStatus.New ||
                this.model.form.status === VisitorRegistrationFormStatus.Draft ||
                this.model.form.status === VisitorRegistrationFormStatus.Rejected) {
                this.options.formState.disabled = false;
                return;
            }

            this.options.formState.disabled = true;
        } finally {
            this.form.markAsPristine({ onlySelf: false });
        }
    }
}
